<script setup>
import { ref, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';

import Button from 'dashboard/components-next/button/Button.vue';
import TramiteFormFillModal from './TramiteFormFillModal.vue';
import tramiteFormResponses from 'dashboard/api/tramiteFormResponses';
import TramiteTypesAPI from 'dashboard/api/tramiteTypes';

const props = defineProps({
  tramiteId: {
    type: Number,
    required: true,
  },
  tramiteTypeId: {
    type: Number,
    required: true,
  },
});

const { t } = useI18n();

const responses = ref([]);
const isLoading = ref(false);

// Ad-hoc dispatch
const adhocForms = ref([]);
const selectedAdhocId = ref(null);
const isDispatching = ref(false);

// Fill modal
const fillModalRef = ref(null);
const activeResponseId = ref(null);

const STATUS_PILL = {
  pending: 'bg-n-amber-3 text-n-amber-11',
  submitted: 'bg-n-teal-3 text-n-teal-11',
};

const load = async () => {
  isLoading.value = true;
  try {
    const { data } = await tramiteFormResponses.get(props.tramiteId);
    responses.value = data || [];
  } catch {
    useAlert(t('TRAMITES.FORMS.LOAD_ERROR'));
    responses.value = [];
  } finally {
    isLoading.value = false;
  }
};

const loadAdhocForms = async () => {
  try {
    const { data } = await TramiteTypesAPI.getForms(props.tramiteTypeId);
    adhocForms.value = (data || []).filter(f => f.role === 'adhoc');
  } catch {
    adhocForms.value = [];
  }
};

const requestForm = async () => {
  if (!selectedAdhocId.value) return;
  isDispatching.value = true;
  try {
    await tramiteFormResponses.dispatch(props.tramiteId, selectedAdhocId.value);
    useAlert(t('TRAMITES.FORMS.REQUEST_SUCCESS'));
    selectedAdhocId.value = null;
    await load();
  } catch {
    useAlert(t('TRAMITES.FORMS.REQUEST_ERROR'));
  } finally {
    isDispatching.value = false;
  }
};

const openFillModal = row => {
  activeResponseId.value = row.id;
  fillModalRef.value?.open();
};

const onSubmitted = () => {
  load();
};

const copyLink = async row => {
  const url = `${window.location.origin}/public/api/v1/tramite_form_responses/${row.public_token}`;
  try {
    await navigator.clipboard.writeText(url);
    useAlert(t('TRAMITES.FORMS.LINK_COPIED'));
  } catch {
    useAlert(t('TRAMITES.FORMS.COPY_LINK_ERROR'));
  }
};

onMounted(() => {
  load();
  loadAdhocForms();
});
</script>

<template>
  <div class="flex flex-col gap-3">
    <!-- Empty state -->
    <p
      v-if="!isLoading && !responses.length"
      class="text-sm text-n-slate-10"
    >
      {{ t('TRAMITES.FORMS.EMPTY') }}
    </p>

    <!-- Loading -->
    <div v-if="isLoading" class="flex justify-center py-4">
      <span class="i-lucide-loader-circle animate-spin size-5 text-n-slate-10" />
    </div>

    <!-- Rows -->
    <div
      v-for="row in responses"
      :key="row.id"
      class="flex items-center gap-3 py-2 border-b border-n-weak/60 last:border-b-0"
      data-test="form-response-row"
    >
      <!-- Form name -->
      <span class="flex-1 min-w-0 text-sm text-n-slate-12 truncate">
        {{ row.form?.name }}
      </span>

      <!-- Status pill -->
      <span
        class="shrink-0 rounded-full px-1.5 py-0.5 text-xs font-medium"
        :class="STATUS_PILL[row.status] || STATUS_PILL.pending"
      >
        {{ row.status === 'submitted' ? t('TRAMITES.FORMS.SUBMITTED') : t('TRAMITES.FORMS.PENDING') }}
      </span>

      <!-- Actions -->
      <Button
        v-if="row.status === 'pending' && row.audience === 'internal'"
        size="xs"
        color="blue"
        :label="t('TRAMITES.FORMS.FILL')"
        @click="openFillModal(row)"
      />

      <Button
        v-else-if="row.status === 'pending' && row.audience === 'citizen'"
        variant="ghost"
        color="slate"
        size="xs"
        icon="i-lucide-link"
        :label="t('TRAMITES.FORMS.COPY_LINK')"
        @click="copyLink(row)"
      />
    </div>

    <!-- Ad-hoc dispatch -->
    <div
      v-if="adhocForms.length"
      class="flex items-center gap-2 pt-2 border-t border-n-weak/60"
      data-test="adhoc-dispatch"
    >
      <select
        v-model="selectedAdhocId"
        class="flex-1 min-w-0 text-sm border border-n-weak rounded px-2 py-1 bg-n-alpha-1 text-n-slate-12"
        data-test="adhoc-select"
      >
        <option :value="null" disabled>{{ t('TRAMITES.FORMS.REQUEST') }}</option>
        <option
          v-for="form in adhocForms"
          :key="form.id"
          :value="form.id"
        >
          {{ form.key }}
        </option>
      </select>
      <Button
        size="xs"
        color="blue"
        :label="t('TRAMITES.FORMS.REQUEST')"
        :disabled="!selectedAdhocId || isDispatching"
        data-test="adhoc-request-btn"
        @click="requestForm"
      />
    </div>

    <!-- Fill modal (shared instance, keyed by activeResponseId) -->
    <TramiteFormFillModal
      ref="fillModalRef"
      :tramite-id="tramiteId"
      :response-id="activeResponseId"
      @submitted="onSubmitted"
    />
  </div>
</template>
